import React, { useState, useEffect } from 'react'
import { EquipmentConfig } from '@/types'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { toastError, toastSuccess } from '@/utils/toast'

interface EquipmentConfigSectionProps {
  initialConfig?: EquipmentConfig
  onSave: (config: EquipmentConfig) => Promise<void>
  readOnly?: boolean
}

export const EquipmentConfigSection: React.FC<EquipmentConfigSectionProps> = ({
  initialConfig,
  onSave,
  readOnly = false,
}) => {
  const [equipmentConfig, setEquipmentConfig] = useState<EquipmentConfig>({
    typeEquipement: '',
    nomFabricant: '',
    nomEquipement: '',
    noEquipement: '',
    noIdentification: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (initialConfig) {
      setEquipmentConfig(initialConfig)
    }
  }, [initialConfig])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setEquipmentConfig(prev => ({
      ...prev,
      [name]: value,
    }))
    if (errors[`equipment.${name}`]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[`equipment.${name}`]
        return newErrors
      })
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!equipmentConfig.typeEquipement.trim()) {
      newErrors['equipment.typeEquipement'] = "Le type d'équipement est requis"
    }
    if (!equipmentConfig.nomFabricant.trim()) {
      newErrors['equipment.nomFabricant'] = 'Le nom du fabricant est requis'
    }
    if (!equipmentConfig.nomEquipement.trim()) {
      newErrors['equipment.nomEquipement'] = "Le nom de l'équipement est requis"
    }
    if (!equipmentConfig.noEquipement.trim()) {
      newErrors['equipment.noEquipement'] = "Le numéro d'équipement est requis"
    }
    if (!equipmentConfig.noIdentification.trim()) {
      newErrors['equipment.noIdentification'] = "Le numéro d'identification est requis"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return
    }

    setIsSaving(true)
    try {
      await onSave(equipmentConfig)
      toastSuccess("Configuration de l'équipement mise à jour avec succès")
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors de la mise à jour de la configuration de l'équipement"
      toastError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Type d'équipement"
            type="text"
            name="typeEquipement"
            value={equipmentConfig.typeEquipement}
            onChange={handleChange}
            error={errors['equipment.typeEquipement']}
            required
            disabled={readOnly}
          />
          <Input
            label="Nom du fabricant"
            type="text"
            name="nomFabricant"
            value={equipmentConfig.nomFabricant}
            onChange={handleChange}
            error={errors['equipment.nomFabricant']}
            required
            disabled={readOnly}
          />
          <Input
            label="Nom de l'équipement"
            type="text"
            name="nomEquipement"
            value={equipmentConfig.nomEquipement}
            onChange={handleChange}
            error={errors['equipment.nomEquipement']}
            required
            disabled={readOnly}
          />
          <Input
            label="Numéro d'équipement"
            type="text"
            name="noEquipement"
            value={equipmentConfig.noEquipement}
            onChange={handleChange}
            error={errors['equipment.noEquipement']}
            required
            disabled={readOnly}
          />
          <Input
            label="Numéro d'identification"
            type="text"
            name="noIdentification"
            value={equipmentConfig.noIdentification}
            onChange={handleChange}
            error={errors['equipment.noIdentification']}
            required
            disabled={readOnly}
          />
        </div>
        {!readOnly && (
          <div className="flex justify-end">
            <Button onClick={handleSubmit} isLoading={isSaving}>
              Enregistrer la configuration
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
